import { enumLabel } from './labels';
import { formatMoney, penceToPoundsInput, poundsToPence } from './money';
import type { FieldDef, RowFilter } from './record-schemas';

export const PAGE_SIZE = 20;
export const PAGE_SIZES = [10, 15, 20, 50, 100] as const;

export function readPageSize(key: string): number {
  const stored = Number(localStorage.getItem(`bo-page:${key}`));
  return (PAGE_SIZES as readonly number[]).includes(stored) ? stored : PAGE_SIZE;
}

export function writePageSize(key: string, size: number): void {
  localStorage.setItem(`bo-page:${key}`, String(size));
}
export type Row = Record<string, unknown>;

export function flattenRow(row: Row): Row {
  const rates = row.ratesJson as { hourlyPence?: unknown; dailyPence?: unknown } | undefined;
  return { ...row, hourlyPence: row.hourlyPence ?? rates?.hourlyPence, dailyPence: row.dailyPence ?? rates?.dailyPence };
}

export function excelCell(row: Row, field: FieldDef, lookups: Record<string, { value: string; label: string }[]> = {}): string | number {
  const value = row[field.key];
  if (field.type === 'money' || field.key === 'gbpAmountPence') {
    if (value == null || value === '') return '';
    const pence = Number(value);
    return Number.isFinite(pence) ? pence / 100 : '';
  }
  if (field.type === 'number') {
    if (value == null || value === '') return '';
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : '';
  }
  return displayField(row, field, lookups);
}

export function displayField(row: Row, field: FieldDef, lookups: Record<string, { value: string; label: string }[]> = {}): string {
  const value = row[field.key];
  if (field.key === 'gbpAmountPence' || field.key === 'gbpNetPence') return value == null || value === '' ? '' : formatMoney(value, 'GBP');
  if (field.type === 'money') return formatMoney(value, String(row.currency || 'GBP'));
  if (field.type === 'checkbox') return value ? 'Yes' : 'No';
  if (field.type === 'tags') return Array.isArray(value) ? value.join(', ') : String(value || '');
  if (field.type === 'textarea') return String(value || '').replace(/\s*\n\s*/g, ', ');
  if (field.lookup) {
    const hit = (lookups[field.lookup] || []).find((option) => option.value === String(value));
    return hit?.label || (value == null ? '' : String(value));
  }
  if (value == null) return '';
  if (field.key === 'status' && row.creditTag === 'credited' && value !== 'credited') return `${enumLabel(String(value))} · Credited`;
  if (field.key === 'status' && row.partCredited) return `${enumLabel(String(value))} · Part credited`;
  if (field.options?.length) return enumLabel(String(value));
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function fold(value: string): string {
  return value.toLowerCase().replace(/[£$€,\s]/g, '');
}

function loose(value: string): string {
  return value.toLowerCase().replace(/[£$€,_\s]/g, '');
}

export function columnContains(text: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  if (text.toLowerCase().includes(needle)) return true;
  const folded = loose(needle);
  return Boolean(folded) && loose(text).includes(folded);
}

export function columnsMatch(cells: Record<string, string>, filters: Record<string, string>): boolean {
  return Object.entries(filters).every(([key, value]) => columnContains(cells[key] || '', value));
}

export function columnParam(filters: Record<string, string>): string {
  const entries = Object.entries(filters).filter(([, value]) => value.trim());
  if (!entries.length) return '';
  return JSON.stringify(Object.fromEntries(entries.map(([key, value]) => [key, value.trim()])));
}

function termMatches(text: string, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  const hay = text.toLowerCase();
  if (hay.includes(needle)) return true;
  const folded = fold(needle);
  return Boolean(folded) && fold(hay).includes(folded);
}

export function textMatches(text: string, query: string): boolean {
  const raw = query.trim();
  if (!raw) return true;
  const groups = raw.split('|').map((part) => part.trim()).filter(Boolean);
  if (!groups.length) return true;
  return groups.some((group) => {
    const terms = group.split(/&|\s+/).map((part) => part.trim()).filter(Boolean);
    return terms.every((term) => termMatches(text, term));
  });
}

export function filterRows(rows: Row[], search: string, fields: FieldDef[], filter: RowFilter | undefined, lookups: Record<string, { value: string; label: string }[]> = {}): Row[] {
  const query = search.trim();
  return rows.filter((row) => {
    if (filter && !filter.match(row)) return false;
    if (!query) return true;
    return textMatches(fields.map((field) => displayField(row, field, lookups)).join(' '), query);
  });
}

export interface ColumnSort {
  key: string;
  dir: 'asc' | 'desc';
}

export function nextSort(current: ColumnSort | null, key: string): ColumnSort {
  if (current?.key === key && current.dir === 'asc') return { key, dir: 'desc' };
  return { key, dir: 'asc' };
}

export function compareCells(a: unknown, b: unknown): number {
  const left = sortCell(a);
  const right = sortCell(b);
  if (left.empty && right.empty) return 0;
  if (left.empty) return -1;
  if (right.empty) return 1;
  if (left.n != null && right.n != null) return left.n - right.n;
  return left.t.localeCompare(right.t, undefined, { numeric: true, sensitivity: 'base' });
}

function sortCell(value: unknown): { empty: boolean; n?: number; t: string } {
  if (value == null || value === '') return { empty: true, t: '' };
  if (typeof value === 'number' && Number.isFinite(value)) return { empty: false, n: value, t: String(value) };
  if (typeof value === 'boolean') return { empty: false, t: value ? 'yes' : 'no' };
  if (Array.isArray(value)) return { empty: !value.length, t: value.map((item) => String(item)).join(', ') };
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const time = Date.parse(text);
    if (!Number.isNaN(time)) return { empty: false, n: time, t: text };
  }
  return { empty: false, t: text };
}

export function sortRows<T>(rows: T[], value: (row: T) => unknown, dir: 'asc' | 'desc'): T[] {
  return [...rows].sort((a, b) => {
    const cmp = compareCells(value(a), value(b));
    return dir === 'asc' ? cmp : -cmp;
  });
}

export interface TableColumn<T> {
  key: string;
  text: (row: T) => unknown;
  sort?: (row: T) => unknown;
}

export function applyTableQuery<T>(rows: T[], search: string, columns: TableColumn<T>[], sort: ColumnSort | null): T[] {
  const query = search.trim();
  const matched = query
    ? rows.filter((row) => textMatches(columns.map((column) => String(column.text(row) ?? '')).join(' '), query))
    : rows;
  if (!sort) return matched;
  const column = columns.find((item) => item.key === sort.key);
  if (!column) return matched;
  return sortRows(matched, column.sort || column.text, sort.dir);
}

export function pageCount(length: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(length / pageSize));
}

export function pageSlice<T>(rows: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export function recordListQuery(input: {
  url: string;
  page: string;
  pageSize: string;
  q?: string;
  sort?: ColumnSort | null;
  filter?: string;
  customerId?: string;
  from?: string;
  to?: string;
  kind?: string;
  status?: string;
  engagementId?: string;
  poId?: string;
  invoiceId?: string;
  billId?: string;
  sql?: string;
  cols?: string;
}): string {
  const [path, existing] = input.url.split('?');
  const params = new URLSearchParams(existing || '');
  params.set('page', input.page);
  params.set('pageSize', input.pageSize);
  if (input.q) params.set('q', input.q);
  if (input.sort) {
    params.set('sort', input.sort.key === 'offer' ? 'title' : input.sort.key);
    params.set('dir', input.sort.dir);
  }
  if (input.filter && input.filter !== 'all') params.set('filter', input.filter);
  if (input.customerId) params.set('customerId', input.customerId);
  if (input.from) params.set('from', input.from);
  if (input.to) params.set('to', input.to);
  if (input.kind) params.set('kind', input.kind);
  if (input.status) params.set('status', input.status);
  if (input.engagementId) params.set('engagementId', input.engagementId);
  if (input.poId) params.set('poId', input.poId);
  if (input.invoiceId) params.set('invoiceId', input.invoiceId);
  if (input.billId) params.set('billId', input.billId);
  if (input.sql) params.set('sql', input.sql);
  if (input.cols) params.set('cols', input.cols);
  return `${path}?${params}`;
}

export function emptyDraft(fields: FieldDef[]): Row {
  const draft: Row = { currency: 'GBP' };
  for (const field of fields) {
    if (field.type === 'checkbox') draft[field.key] = true;
    else if (field.type === 'tags') draft[field.key] = [];
    else if (field.type === 'select' && field.options?.length) draft[field.key] = field.options[0];
    else draft[field.key] = '';
  }
  return draft;
}

export function editDraft(row: Row, fields: FieldDef[]): Row {
  const draft: Row = { ...row };
  for (const field of fields) {
    if (field.type === 'money') draft[field.key] = penceToPoundsInput(row[field.key]);
    if (field.type === 'tags') draft[field.key] = Array.isArray(row[field.key]) ? [...(row[field.key] as unknown[])] : [];
  }
  return draft;
}

export function toPayload(form: Row, fields: FieldDef[]): Row {
  const body: Row = { ...form };
  for (const field of fields) {
    if (field.type === 'money') body[field.key] = poundsToPence(form[field.key]);
    if (field.type === 'number') body[field.key] = Number(form[field.key] || 0);
    if (field.type === 'tags') body[field.key] = Array.isArray(form[field.key]) ? form[field.key] : [];
  }
  return body;
}

export interface SalesOffer {
  id: string;
  customerId: string;
  customerName: string;
  title: string;
  number: string;
  revision: string;
  status: string;
  proposalId?: string;
  quotationId?: string;
  updated?: string;
}

function joined(left: unknown, right: unknown): string {
  const a = String(left || '');
  const b = String(right || '');
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} / ${b}`;
}

export function pairSalesOffers(rows: Row[]): SalesOffer[] {
  const proposals = rows.filter((row) => row.kind === 'proposal');
  const quotations = rows.filter((row) => row.kind === 'quotation');
  const used = new Set<Row>();
  const offers: SalesOffer[] = [];
  for (const proposal of proposals) {
    const quotation = quotations.find((row) => !used.has(row) && String(row.proposalId || '') === String(proposal.id));
    if (quotation) used.add(quotation);
    offers.push({
      id: quotation ? `${proposal.id}:${quotation.id}` : String(proposal.id),
      customerId: String(proposal.customerId || quotation?.customerId || ''),
      customerName: String(proposal.customerName || quotation?.customerName || ''),
      title: String(proposal.title || quotation?.title || ''),
      number: joined(proposal.number, quotation?.number),
      revision: joined(proposal.revision ?? 1, quotation ? quotation.revision ?? 1 : ''),
      status: joined(proposal.status, quotation?.status),
      proposalId: String(proposal.id),
      quotationId: quotation ? String(quotation.id) : undefined,
      updated: String(proposal.issuedOn || quotation?.issuedOn || ''),
    });
  }
  for (const quotation of quotations) {
    if (used.has(quotation)) continue;
    offers.push({
      id: String(quotation.id),
      customerId: String(quotation.customerId || ''),
      customerName: String(quotation.customerName || ''),
      title: String(quotation.title || ''),
      number: String(quotation.number || ''),
      revision: String(quotation.revision ?? 1),
      status: String(quotation.status || ''),
      quotationId: String(quotation.id),
      updated: String(quotation.issuedOn || ''),
    });
  }
  return offers;
}

export function deleteUrl(schemaUrl: string, row: Row, id: unknown): string {
  if (row.kind === 'proposal') return `/api/proposals/${id}`;
  if (row.kind === 'quotation') return `/api/quotations/${id}`;
  return `${schemaUrl}/${id}`;
}
